import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import styles from './work4.module.scss';
import Contact from '../../components/Contact/Contact';

import heroImage from '../../assets/ex8.jpg';
import image1 from '../../assets/Work2-1.png';
import image2 from '../../assets/Work2-2.png';
import image3 from '../../assets/crm1.png';
import image4 from '../../assets/crm2.png';
import image5 from '../../assets/crm3.png';
import image6 from '../../assets/crm4.png';
import icon1 from '../../assets/workicon2-1.png';
import icon2 from '../../assets/workicon2-2.png';
import icon3 from '../../assets/workicon2-3.png';
import SolutionDiagram from '../SolutionDiagram';

interface WorkImage {
  id: number;
  src: string;
  alt: string;
  text: string;
}

interface CircleFeature {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
}

interface ImageItem {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
}

const Work4: React.FC = () => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const [imagesVisible, setImagesVisible] = useState<boolean[]>([false, false, false]);
  const [titleVisible, setTitleVisible] = useState<boolean>(false);
  const [descriptionVisible, setDescriptionVisible] = useState<boolean>(false);
  const [featuresVisible, setFeaturesVisible] = useState<boolean>(false);
  const [galleryVisible, setGalleryVisible] = useState<boolean>(false);
  const [sectionTitlesVisible, setSectionTitlesVisible] = useState<boolean[]>([false]);
  const imagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const titleRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const sectionTitleRefs = useRef<(HTMLDivElement | null)[]>([null]);
  
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const slideRef = useRef<HTMLDivElement>(null);
  // 슬라이드 전환 상태를 추적하기 위한 ref 추가
  const isTransitioning = useRef(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkIsMobile();
    
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const workImages: WorkImage[] = [
    {
      id: 1,
      src: image1,
      alt: "전자상거래 플랫폼 메인 화면",
      text: "아직 못정했습니다"
    }
  ];

  const circleFeatures: CircleFeature[] = [
    {
      id: 1,
      src: icon1,
      alt: "키워드 자동입찰",
      title: "키워드 자동입찰",
      description: "광고를 원하는 시간에\n원하는 순위로 입찰 가능"
    },
    {
      id: 2,
      src: icon2,
      alt: "스케쥴 및 지역설정",
      title: "스케쥴 및 지역설정",
      description: "시간별로 원하는 입찰 세팅,\n17개의 시,도,제주도의\n구,동,면까지 디테일한 설정."
    },
    {
      id: 3,
      src: icon3,
      alt: "광고비 효율적 관리",
      title: "광고비 효율적 관리",
      description: "입찰금액 변화 추이 제공,\n데이터를 저장하여 분석 가능"
    }
  ];

  const galleryImages: ImageItem[] = [
    {
      id: 1,
      src: icon1,
      alt: "키워드 자동입찰",
      title: "키워드 자동입찰",
      description: "광고를 원하는 시간에, 원하는 순위로 입찰을 설정하여\n시간대별 성과에 맞춘 자동화 운영이 가능합니다.\n🡒 시간/순위 설정 → 자동 입찰 실행 → 효율 극대화"
    },
    {
      id: 2,
      src: icon2,
      alt: "스케쥴 및 지역설정",
      title: "스케쥴 및 지역설정",
      description: "원하는 요일, 시간은 물론 17개 시·도부터\n구, 동, 면 단위까지 세밀하게 타겟팅 설정이 가능합니다.\n🡒 시간대·지역 설정 → 세분화 타겟팅 → 맞춤 입찰 운영"
    },
    {
      id: 3,
      src: icon3,
      alt: "광고비 효율적 관리",
      title: "광고비 효율적 관리",
      description: "입찰 단가 변동 이력을 저장하고 분석할 수 있어\n불필요한 지출을 줄이고 전략적인 운영이 가능합니다.\n🡒 입찰 데이터 저장 → 변화 추이 분석 → 효율적 예산 관리"
    },
    {
      id: 4,
      src: icon3,
      alt: "플랫폼 연동 및 수정기능 ",
      title: "플랫폼 연동 및 수정",
      description: "API를 통해 광고 플랫폼과 연동하고,\n가져온 데이터를 솔루션 내에서 직접 수정하고 관리할 수 있습니다.\n🡒 플랫폼 연동 → 실시간 데이터 수신 → 내부에서 직접 제어"
    }
  ];

  // 슬라이드 변경 함수 - 중복 실행 및 2장씩 이동하는 문제 해결
  const changeSlide = useCallback((direction: 'next' | 'prev') => {
    // 이미 슬라이드 전환 중이면 무시
    if (isTransitioning.current) {
      console.log("Ignoring slide change because transition is in progress");
      return;
    }
    
    // 슬라이드 전환 플래그 설정
    isTransitioning.current = true;
    console.log("Starting slide transition to", direction);
    
    setCurrentImageIndex(prevIndex => {
      let newIndex;
      if (direction === 'next') {
        newIndex = (prevIndex + 1) % galleryImages.length;
      } else {
        newIndex = (prevIndex - 1 + galleryImages.length) % galleryImages.length;
      }
      console.log("Changing slide from", prevIndex, "to", newIndex);
      // 상태 업데이트를 한 번에 처리
      setSelectedImage(galleryImages[newIndex]);
      return newIndex;
    });
    
    // 슬라이드 전환 완료 후 플래그 해제 (300ms 딜레이)
    setTimeout(() => {
      isTransitioning.current = false;
      console.log("Transition complete, allowing next slide change");
    }, 300);
  }, [galleryImages]);
  
  // 다음 이미지로 이동
  const goToNextImage = useCallback(() => {
    changeSlide('next');
  }, [changeSlide]);
  
  // 이전 이미지로 이동
  const goToPrevImage = useCallback(() => {
    changeSlide('prev');
  }, [changeSlide]);

  // 터치 이벤트 핸들러 (모바일 스와이프) 개선
  const handleTouchStart = (e: React.TouchEvent) => {
    // 이미 슬라이드 전환 중이면 무시
    if (isTransitioning.current) return;
    
    setTouchStart(e.targetTouches[0].clientX);
    setIsSliding(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSliding) return;
    
    setTouchEnd(e.targetTouches[0].clientX);
    
    if (slideRef.current) {
      const moveDistance = (e.targetTouches[0].clientX - touchStart) / 10;
      slideRef.current.style.transform = `translateX(${moveDistance}px)`;
    }
  };

  const handleTouchEnd = () => {
    // 이미 슬라이드 전환 중이면 무시
    if (isTransitioning.current) return;
    
    setIsSliding(false);
    
    if (slideRef.current) {
      slideRef.current.style.transform = 'translateX(0)';
      slideRef.current.style.transition = 'transform 0.3s ease';
      
      setTimeout(() => {
        if (slideRef.current) {
          slideRef.current.style.transition = '';
        }
      }, 300);
    }
    
    // 스와이프 거리가 충분할 때만 슬라이드 변경
    if (touchStart - touchEnd > 30) {
      changeSlide('next');
    } else if (touchStart - touchEnd < -30) {
      changeSlide('prev');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNextImage();
      } else if (e.key === 'ArrowLeft') {
        goToPrevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNextImage, goToPrevImage]);

  useEffect(() => {
    if (galleryImages.length > 0) {
      setSelectedImage(galleryImages[0]);
    }
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const titleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTitleVisible(true);
          titleObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const featuresObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setFeaturesVisible(true);
          featuresObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const imageObservers = imagesRef.current.map((ref, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setImagesVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            observer.unobserve(entry.target);
          }
        });
      }, { ...observerOptions, threshold: 0.2 });
      return observer;
    });

    const galleryObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setGalleryVisible(true);
          galleryObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const descriptionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setDescriptionVisible(true);
          descriptionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sectionTitleObservers = sectionTitleRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setSectionTitlesVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);
      return observer;
    });

    if (titleRef.current) {
      titleObserver.observe(titleRef.current);
    }

    if (featuresRef.current) {
      featuresObserver.observe(featuresRef.current);
    }

    imagesRef.current.forEach((ref, index) => {
      if (ref) imageObservers[index].observe(ref);
    });

    if (galleryRef.current) {
      galleryObserver.observe(galleryRef.current);
    }

    if (descriptionRef.current) {
      descriptionObserver.observe(descriptionRef.current);
    }

    sectionTitleRefs.current.forEach((ref, index) => {
      if (ref) sectionTitleObservers[index].observe(ref);
    });

    return () => {
      if (titleRef.current) titleObserver.unobserve(titleRef.current);
      
      if (featuresRef.current) featuresObserver.unobserve(featuresRef.current);
      
      imagesRef.current.forEach((ref, index) => {
        if (ref) imageObservers[index].unobserve(ref);
      });
      
      if (galleryRef.current) galleryObserver.unobserve(galleryRef.current);
      
      if (descriptionRef.current) descriptionObserver.unobserve(descriptionRef.current);
      
      sectionTitleRefs.current.forEach((ref, index) => {
        if (ref) sectionTitleObservers[index].unobserve(ref);
      });
    };
  }, []);

  return (
    <div className={`${styles.work4Container} ${mode === 'dark' ? styles.dark : ''}`}>
        <div 
          ref={titleRef} 
          className={`${styles.titleSection} ${titleVisible ? styles.visible : ''}`}
        >
          <h1 className={styles.title}>검색광고 자동입찰 솔루션</h1>
          <div className={styles.overlay}></div>
          <img 
            src={heroImage} 
            alt="전자상거래 솔루션" 
            className={styles.backgroundImage} 
          />
        </div>
        <p className={styles.subtitle} 
           dangerouslySetInnerHTML={{ 
             __html: "복잡한 자동입찰 효율적으로 관리하세요<br />키워드 관리, 순위확인, 지역설정까지<br /><span class=\"" + styles.impact + "\">minimize</span>의 자동입찰 솔루션으로<br/>시간과 비용을 절약해보세요" 
           }}>
        </p>
      
      <div
        ref={featuresRef}
        className={`${styles.featuresSection} ${featuresVisible ? styles.visible : ''}`}
      >
        <div className={styles.featuresContainer}>
          {circleFeatures.map((feature, index) => (
            <div 
              key={feature.id} 
              className={styles.featureItem}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className={styles.circleImageContainer}>
                <img src={feature.src} alt={feature.alt} className={styles.circleImage} />
              </div>
              <div className={styles.featureContent}>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>
                  {feature.description.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < feature.description.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div 
        ref={el => {sectionTitleRefs.current[0] = el}} 
        className={`${styles.sectionTitle} ${sectionTitlesVisible[0] ? styles.visible : ''}`}
      >
        <h2>검색광고 자동입찰 솔루션</h2>
      </div>
      
      <div 
        ref={galleryRef}
        className={`${styles.galleryPreviewSection} ${galleryVisible ? styles.visible : ''}`}
      >
        <div 
          className={styles.galleryContainer}
          ref={slideRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.topSection}>
            {/* 네비게이션 버튼 (왼쪽) */}
            <button 
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToPrevImage();
              }}
              aria-label="Previous Image"
            >
              <span className={styles.arrowSymbol}>&#10094;</span>
            </button>
            
            <div className={styles.imageSection}>
              <div className={styles.mainImageWrapper}>
                {selectedImage && (
                  <img 
                    src={selectedImage.src} 
                    alt={selectedImage.alt}
                    className={styles.mainImage}
                  />
                )}
              </div>
            </div>
            
            <div className={styles.textSection}>
              {selectedImage && (
                <>
                  <div className={styles.slideIndicator}>
                    <span className={styles.currentSlide}>{currentImageIndex + 1}</span>
                    <span className={styles.totalSlides}>/ {galleryImages.length}</span>
                  </div>
                  <h2 className={styles.imageTitle}>{selectedImage.title}</h2>
                  <p className={styles.imageDescription}>
                    {selectedImage.description.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < selectedImage.description.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </>
              )}
            </div>
            
            {/* 네비게이션 버튼 (오른쪽) */}
            <button 
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToNextImage();
              }}
              aria-label="Next Image"
            >
              <span className={styles.arrowSymbol}>&#10095;</span>
            </button>
          </div>
          
          <div className={`${styles.bottomSection} ${isMobile ? styles.hiddenOnMobile : ''}`}>
            <div className={styles.thumbnailContainer}>
              {galleryImages.map((image) => (
                <div 
                  key={image.id}
                  className={`${styles.thumbnailItem} ${selectedImage && selectedImage.id === image.id ? styles.active : ''}`}
                  // onClick 이벤트 핸들러 제거
                >
                  <div className={styles.thumbnailImageWrapper}>
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className={styles.thumbnailImage}
                    />
                  </div>
                  <div className={styles.thumbnailContent}>
                    <span className={styles.thumbnailNumber}>0{image.id}</span>
                    <h3 className={styles.thumbnailTitle}>{image.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.imageGallery}>
        {workImages.map((image, index) => (
          <div 
            key={image.id}
            ref={(el) => { imagesRef.current[index] = el }}
            className={`${styles.imageContainer} ${imagesVisible[index] ? styles.visible : ''}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <div className={styles.imageWrapper}>
              <img src={image.src} alt={image.alt} className={styles.workImage} />
            </div>
            <p className={styles.imageText}>{image.text}</p>
          </div>
        ))}
      </div>
      
      <div 
        ref={descriptionRef}
        className={`${styles.descriptionSection} ${descriptionVisible ? styles.visible : ''}`}
      >
        <div className={styles.descriptionWrapper}>
          <h2 className={styles.descriptionTitle}>프로젝트 상세 설명</h2>
          <p className={styles.descriptionText}>
            미니마이즈 전자상거래 솔루션은 브랜드와 고객을 효과적으로 연결하고 매출을 극대화하기 위한 종합 플랫폼입니다.
          </p>
          <p className={styles.descriptionText}>
            사용자 친화적인 인터페이스와 개인화된 쇼핑 경험으로 고객 만족도를 높이고, 효율적인 재고 및 주문 관리 시스템으로 운영 효율성을 개선합니다.
          </p>
          <p className={styles.descriptionText}>
            데이터 기반 인사이트와 마케팅 도구를 활용해 전략적인 비즈니스 의사결정을 지원하고 판매 성과를 향상시킵니다.
          </p>
          <p className={styles.descriptionText}>온라인 비즈니스의 잠재력을 최대한 활용하는 전자상거래 솔루션을 경험해보세요.</p>
        </div>
      </div>
      
      <Contact 
        backgroundColor={mode === 'dark' ? '#2a2a2a' : '#f9f9f9'}
        textColor={mode === 'dark' ? '#f0f0f0' : '#333'}
      />
    </div>
  );
};

export default Work4;